import React from "react";
import {TableOutlined} from "@ant-design/icons";
import {Table, Text, TextAs, TextSizes, EmptyState} from "@clockout-dev/design-elements";
import SectionCard from "../../common/sectionCard/SectionCard";
import styles from "../styles/results.module.scss";

function formatValue(value) {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

function humanizeKey(key) {
    return String(key).replace(/_/g, " ");
}

/**
 * Mode-aware extraction view. Shift extractions (`extracted.shifts`) render
 * as a small table with columns derived from the rows themselves (the shape
 * varies by document); everything else renders as a key/value grid. Fully
 * tolerant of a missing or empty `extracted` object.
 *
 * @param {Object} props
 * @param {Object} [props.verdict] — entry from report `verdicts`
 */
function ExtractedFieldsCard({verdict}) {
    const extracted = verdict?.extracted && typeof verdict.extracted === "object" ? verdict.extracted : null;
    const shifts = Array.isArray(extracted?.shifts) ? extracted.shifts : null;

    let body = null;
    if (shifts) {
        const columnKeys = [...new Set(shifts.flatMap(row => (row && typeof row === "object" ? Object.keys(row) : [])))];
        const columns = columnKeys.map(key => ({
            title: humanizeKey(key),
            dataIndex: key,
            key,
            render: value => formatValue(value)
        }));
        body = (
            <div className={styles.tableScroll}>
                <Table
                    columns={columns}
                    dataSource={shifts.map((row, index) => ({...row, __key: index}))}
                    rowKey="__key"
                    pagination={false}
                    emptyState={<EmptyState title="No shifts extracted" />}
                />
            </div>
        );
    } else if (extracted && Object.keys(extracted).length) {
        body = (
            <div className={styles.kvGrid}>
                {Object.entries(extracted).map(([key, value]) => (
                    <div key={key} className={styles.kvRow}>
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.kvLabel}>
                            {humanizeKey(key)}
                        </Text>
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.kvValue}>
                            {formatValue(value)}
                        </Text>
                    </div>
                ))}
            </div>
        );
    } else {
        body = (
            <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.emptyNote}>
                Nothing was extracted from this document.
            </Text>
        );
    }

    return (
        <SectionCard icon={<TableOutlined />} title={shifts ? `Extracted Shifts (${shifts.length})` : "Extracted Fields"}>
            {body}
        </SectionCard>
    );
}

export default ExtractedFieldsCard;
